/**
 * Routes.
 *
 * Stage 2 covers the entry and auth screens. The planning surfaces arrive with their contexts.
 *
 * A note that matters for every route added later: **route-level guarding is presentation, not
 * security.** Hiding a route stops a confusing dead end; it does not protect anything. Every
 * request the route makes is authorized server-side, and a private resource returns 404 there
 * regardless of what the client believes (docs/engineering/04-authorization.md).
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { AppShell } from './components/composites/AppShell';

import { LandingScreen } from './features/landing/LandingScreen';
import { NotYetScreen } from './features/landing/NotYetScreen';
import { AccessUnavailableScreen } from './features/capabilities/AccessUnavailableScreen';
import { AccessScreen } from './features/capabilities/AccessScreen';
import { ParticipantScreen } from './features/participate/ParticipantScreen';
import { TemplateLibraryScreen } from './features/templates/TemplateLibraryScreen';
import { TemplateEditorScreen } from './features/templates/TemplateEditorScreen';
import { CreateTemplateScreen } from './features/templates/CreateTemplateScreen';
import { DashboardScreen } from './features/liturgies/DashboardScreen';
import { CreateLiturgyScreen } from './features/liturgies/CreateLiturgyScreen';
import { CreatedScreen } from './features/liturgies/CreatedScreen';
import { OverviewScreen } from './features/liturgies/OverviewScreen';
import { PlanningScreen } from './features/liturgies/PlanningScreen';
import { PeopleScreen } from './features/people/PeopleScreen';
import { AttachmentsScreen } from './features/attachments/AttachmentsScreen';
import { ExportsScreen } from './features/exports/ExportsScreen';
import { HistoryScreen } from './features/history/HistoryScreen';
import { NotificationsScreen } from './features/notifications/NotificationsScreen';
import { IntegrationsScreen } from './features/integrations/IntegrationsScreen';
import { OrganizationsScreen } from './features/organizations/OrganizationsScreen';
import { SiteAdminScreen } from './features/organizations/SiteAdminScreen';
import { OrganizationAdminScreen } from './features/organizations/OrganizationAdminScreen';
import { SelectionPolicyScreen } from './features/policies/SelectionPolicyScreen';
import { GenericPolicyEditorScreen } from './features/policies/GenericPolicyEditorScreen';
import { PolicyLibraryScreen } from './features/policies/PolicyLibraryScreen';
import { RegisterScreen } from './features/auth/RegisterScreen';
import { SignInScreen } from './features/auth/SignInScreen';
import {
  CompletePasswordResetScreen,
  RequestPasswordResetScreen,
} from './features/auth/PasswordResetScreen';
import { VerifyEmailScreen } from './features/auth/VerifyEmailScreen';
import { MfaScreen } from './features/auth/MfaScreen';
import { PreferencesScreen } from './features/preferences/PreferencesScreen';

/**
 * The shell wraps every route.
 *
 * Screens that carry their own chrome — the landing page, the auth screens, the participant view —
 * are excluded inside `AppShell` rather than by being routed elsewhere, so the exclusion is one
 * list in one file instead of a convention each new screen has to remember.
 */
const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingScreen,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    if (typeof search.redirect === 'string' && search.redirect.startsWith('/')) {
      return { redirect: search.redirect };
    }
    return {};
  },
  component: SignInScreen,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    if (typeof search.redirect === 'string' && search.redirect.startsWith('/')) {
      return { redirect: search.redirect };
    }
    return {};
  },
  component: RegisterScreen,
});

const passwordResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/password-reset',
  component: RequestPasswordResetScreen,
});

const passwordResetCompleteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/password-reset/complete',
  component: CompletePasswordResetScreen,
  // The token arrives in the query string of an emailed link. Validated as a string here and
  // treated as untrusted input beyond that — the server decides whether it is real.
  validateSearch: (search: Record<string, unknown>): { token?: string | undefined } => ({
    token: typeof search['token'] === 'string' ? search['token'] : undefined,
  }),
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailScreen,
  validateSearch: (search: Record<string, unknown>): { token?: string | undefined } => ({
    token: typeof search['token'] === 'string' ? search['token'] : undefined,
  }),
});

/**
 * Destinations the landing page links to, registered now so those links are type-checked against a
 * real route rather than bypassing the router's typed-link guarantee with a bare <a href>.
 *
 * Each renders NotYetScreen until its stage lands. Replace the component, keep the route.
 */
const liturgiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies',
  component: () => <DashboardScreen collection="mine" />,
});

/**
 * The dashboard's other two collections.
 *
 * A route each rather than component state, so a collection is deep-linkable, survives a reload,
 * and can be linked to from elsewhere. All three render the same screen over the same one authorized
 * response — the route selects a view of it and never widens it (docs/engineering/11-screens.md).
 *
 * Declared before the $id route, like "new", so neither word is read as an id.
 */
const sharedLiturgiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/shared',
  component: () => <DashboardScreen collection="shared" />,
});

const archivedLiturgiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/archived',
  component: () => <DashboardScreen collection="archived" />,
});

// Declared before the $id route so "new" is not read as an id.
const newLiturgyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/new',
  component: CreateLiturgyScreen,
});

const liturgyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id',
  component: OverviewScreen,
});

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/planning',
  validateSearch: (search: Record<string, unknown>): { item?: string } => {
    if (typeof search.item === 'string') return { item: search.item };
    return {};
  },
  component: PlanningScreen,
});

const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/people',
  component: PeopleScreen,
});

const attachmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/attachments',
  component: AttachmentsScreen,
});

/**
 * Where a redeemed **Participant** link lands. A separate surface from the workspace rather than the
 * workspace with things hidden — see ParticipantScreen.
 */
const participateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/participate',
  component: ParticipantScreen,
});

const accessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/access',
  component: AccessScreen,
});

/** Shown once, after creating a celebration without an account. */
const createdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/created',
  component: CreatedScreen,
});

const historyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/history',
  component: HistoryScreen,
});

const exportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/exports',
  component: ExportsScreen,
});

const selectionPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/liturgies/$id/selection-policy',
  component: SelectionPolicyScreen,
});

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates',
  component: TemplateLibraryScreen,
});

// Declared before the $id route so "new" is not read as a template id.
const newTemplateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates/new',
  component: CreateTemplateScreen,
});

/** Screen 20 — four panes: Structure, Constraints, Localization, Versions. */
const templateEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates/$id/edit',
  component: TemplateEditorScreen,
});

/** Beside /templates, not inside a Liturgy: a Policy is a reusable resource in the same sense. */

const orgPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizations/$id/selection-policy',
  component: () => <GenericPolicyEditorScreen scope="organization" />,
});

const clericPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me/selection-policy',
  component: () => <GenericPolicyEditorScreen scope="cleric" />,
});

const policiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/policies',
  component: PolicyLibraryScreen,
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: NotificationsScreen,
});

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integrations',
  component: IntegrationsScreen,
});

const organizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizations',
  component: OrganizationsScreen,
});

/** Screen 22 — governance of one Organization, and of nothing anybody plans within it. */
const organizationAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizations/$id/admin',
  component: OrganizationAdminScreen,
});

/**
 * Site administration.
 *
 * Routed like any other page and guarded only server-side, deliberately: hiding a route stops a
 * confusing dead end, it does not protect anything. Somebody who reaches this without authority
 * gets a plain explanation rather than a 403 page, because for almost everyone that is the ordinary
 * and correct state rather than an error.
 */
const siteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site',
  beforeLoad: () => {
    throw redirect({ to: '/site/organization-requests' });
  },
});

const siteRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site/organization-requests',
  component: () => <SiteAdminScreen defaultTab="requests" />,
});

const siteOrganizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site/organizations',
  component: () => <SiteAdminScreen defaultTab="organizations" />,
});

const siteAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site/accounts',
  component: () => <SiteAdminScreen defaultTab="accounts" />,
});

const siteTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site/templates',
  component: () => <SiteAdminScreen defaultTab="templates" />,
});

const siteAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site/audit',
  component: () => <SiteAdminScreen defaultTab="audit" />,
});

const siteIntegrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/site/integrations',
  component: () => <SiteAdminScreen defaultTab="integrations" />,
});

/** Screen 18 — per-Account interface settings, including one of the four language axes. */
const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preferences',
  component: PreferencesScreen,
});

/**
 * Two-step verification. Beside the auth screens rather than inside site administration, because it
 * belongs to the Account: somebody can enrol before anyone designates them, and should be able to.
 */
const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: MfaScreen,
});

/**
 * Where the server redirects every failed redemption — unknown, revoked, expired, replayed. One
 * destination for all of them, deliberately; see AccessUnavailableScreen.
 */
const accessUnavailableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/access-unavailable',
  component: AccessUnavailableScreen,
});

const routeTree = rootRoute.addChildren([
  accessUnavailableRoute,
  liturgiesRoute,
  sharedLiturgiesRoute,
  archivedLiturgiesRoute,
  newLiturgyRoute,
  liturgyRoute,
  planningRoute,
  peopleRoute,
  attachmentsRoute,
  exportsRoute,
  historyRoute,
  participateRoute,
  accessRoute,
  createdRoute,
  selectionPolicyRoute,
  templatesRoute,
  newTemplateRoute,
  templateEditorRoute,
  policiesRoute,
  orgPolicyRoute,
  clericPolicyRoute,
  notificationsRoute,
  integrationsRoute,
  organizationsRoute,
  organizationAdminRoute,
  siteRoute,
  siteRequestsRoute,
  siteOrganizationsRoute,
  siteAccountsRoute,
  siteTemplatesRoute,
  siteAuditRoute,
  siteIntegrationsRoute,
  securityRoute,
  preferencesRoute,
  indexRoute,
  loginRoute,
  registerRoute,
  passwordResetRoute,
  passwordResetCompleteRoute,
  verifyEmailRoute,
]);

export const router = createRouter({
  routeTree,
  // Rather than a blank screen, an unbuilt destination says which stage brings it. The landing
  // page links to real routes on purpose — see NotYetScreen.
  defaultNotFoundComponent: NotYetScreen,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
